import axios from 'axios';
import router from '@/router';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints, OAuth, UserTypes, ID, Views, ViewModes } from '@/ts/store/Constants';
import PersonData from '@/ts/PersonData';
import UserRoleData from '@/ts/UserRoleData';
import AuthData from '@/ts/AuthData';
import { UserResponseData, MiniatureImageOptions, ChangePasswordRequestData, TrackingData } from '@/ts/HococoInterfaces';
import { Vue } from 'vue-property-decorator';
import ErrorResponse from '@/ts/ErrorResponse';

import type { PushNotificationSchema, Token, ActionPerformed } from '@capacitor/push-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import { App, AppInfo } from '@capacitor/app';

import { FCM } from '@capacitor-community/fcm';
import { deleteState } from '@/storage';
import { Connection } from '@/configuration/Connection';
import { LocalizationHelpers } from '@/ts/helpers/LocalizationHelpers';
import { NotifyHelpers } from '@/ts/helpers/NotifyHelpers';



@Module({name: 'User', namespaced: true})
export default class User extends VuexModule {
    public email: string = '';
    public password: string = '';
    public authData: AuthData | null = null;
    public personData: PersonData | null = null;
    public loggedIn: boolean = false;
    public loggingIn: boolean = false;
    public userCache: PersonData[] = [];
    public userCacheMap: { [index: number]: number } = {};
    public personSuggestions: number[] = [];
    public userType: string = UserTypes.EMPLOYEE;
    public userRoles: UserRoleData[] = [];
    public userRolesMap: { [index: number]: number } = {};
    public userClone: PersonData | null = null;
    public responseCode: number | null = null;
    public sandbox: boolean = false;
    public resetPasswordEmail: string = '';
    public resettingPassword: boolean = false;
    public resetPasswordEmailSent: boolean = false;
    public associatedUsers: number[] = [];
    public firebaseToken: Token = null;
    public firebaseConnected: boolean = false;
    public floorPlanBase64: string = '';
    public variantWithFloorPlan: number = null;
    public propertyVariantId: number = null;
    public miniaturesFetched: { [index: number]: boolean } = {};
    public changingPassword: boolean = false;
    public passwordChangeResponseCode: number = null;
    public savedFullName: string = '';
    public profileUpdateResponse: any = null;
    public newProfileImage: any = null;

    public wrongPinCounter: number = 0;
    public wrongPin: boolean = false;
    public pinInput: string[] = [];
    public currentVersion: any = '';
    public availableUpdateUrl: any = '';
    public lastVersion: any = null;
    public staffNotifications: any = [];
    public availableUpdateVisible: boolean = true;
    public dashboardItemsPerPageCount: any = 10;

    public selectedLanguage: string | null;
    public corporationDefaultLanguage: string;

    @Mutation
    public RESET_USER_STORE_STATE(): void {
        this.email                      = '';
        this.password                   = '';
        this.authData                   = null;
        this.personData                 = null;
        this.loggedIn                   = false;
        this.loggingIn                  = false;
        this.userCache                  = [];
        this.userCacheMap               = {};
        this.personSuggestions          = [];
        this.userType                   = UserTypes.EMPLOYEE;
        this.userRoles                  = [];
        this.userRolesMap               = {};
        this.userClone                  = null;
        this.responseCode               = null;
        this.sandbox                    = false;
        this.resetPasswordEmail         = '';
        this.resettingPassword          = false;
        this.resetPasswordEmailSent     = false;
        this.associatedUsers            = [];
        this.firebaseToken              = null;
        this.firebaseConnected          = false;
        this.floorPlanBase64            = '';
        this.variantWithFloorPlan       = null;
        this.propertyVariantId          = null;
        this.miniaturesFetched          = {};
        this.changingPassword           = false;
        this.passwordChangeResponseCode = null;
        this.savedFullName              = '';
        this.profileUpdateResponse      = null;
        this.newProfileImage            = null;
        this.wrongPin                   = false;
        this.pinInput                   = [];
        this.currentVersion             = '';
        this.availableUpdateUrl         = '';
        this.lastVersion                = null;
        this.staffNotifications         = [];
        this.availableUpdateVisible     = true;
        this.dashboardItemsPerPageCount = 10;
        this.selectedLanguage           = null;
        this.corporationDefaultLanguage = null;
    }

    get getCurrentVersion(): any {
        return this.currentVersion;
    }

    get getLastVersion(): any {
        return this.lastVersion;
    }

    get getAvailableUpdateUrl(): any {
        return this.availableUpdateUrl;
    }

    get getAvailableUpdateVisible(): any {
        if (this.lastVersion && !this.lastVersion.is_released) {
            return false;
        }

        return this.availableUpdateVisible;
    }

    get getPropertyVariantId() {
        return User.state.propertyVariantId;
    }

    get isSandboxUser(): boolean {
        return this.sandbox;
    }

    get getResponseCodeMessage() {
        switch (User.state.responseCode) {
            case 401:
            case 400:
                return 'Either your username or password was incorrect. Please try again or retrieve a new password via Forgot Password.';
            case 403:
                return 'You tried to access a resource you dont have access too';
            case 422:
                return 'Wrong pin, try again';
            case 429:
                return 'Too many log-in attempts. Please try again a bit later.';
            default:
                return null;
        }
    }

    get getResponseCode() {
        if (User.state.responeCode !== null) {
            return User.state.responseCode;
        }

        return null;
    }

    get getProfileUpdateResponse() {
        return this.profileUpdateResponse;
    }

    get getNewProfileImage() {
        return this.newProfileImage;
    }

    get getAssociatedUsers() {
        return User.state.associatedUsers;
    }

    get getFloorPlanBase64() {
        return User.state.floorPlanBase64;
    }

    get getVariantWithFloorPlan() {
        return User.state.variantWithFloorPlan;
    }

    get userIsLoggedIn() {
        return User.state.loggedIn;
    }

    get getUserMainRole() {
        return (User.state.personData !== null) ? User.state.personData.mainRole : '';
    }

    get getEmail() {
        return User.state.email;
    }

    get getPasswordResetEmail() {
        return User.state.passwordResetEmail;
    }

    get getPassword() {
        return User.state.password;
    }

    get getUserId() {
        return (User.state.personData !== null) ? User.state.personData.id : ID.NO_USER;
    }

    get getUser() {
        return User.state.personData;
    }

    get getClonedUser() {
        return User.state.userClone;
    }

    get formattedNumber() {
        return '+45 223250450';
        return User.state.personData.formattedNumber;
    }

    get getCacheUser() {
        return (userId: number) => {
            if (User.state.personData !== null && userId === User.state.personData.id) {
                return User.state.personData;
            }

            return User.state.userCache[User.state.userCacheMap[userId]];
        };
    }

    get getCacheRole() {
        return (roleId: number) => {
            return User.state.userRoles[User.state.userRolesMap[roleId]];
        };
    }

    get getCachedRoleId() {
        return (roleKey: string) => {
            return User.state.userRoles.find((role: any) => role.key === roleKey).id;
        };
    }

    get getPersonSuggestions() {
        return User.state.personSuggestions;
    }

    get isEmployee() {
        // TODO(CKJ) : Fix!
        return true;
    }

    get getPropertyId() {
        if (!User.state.personData) {
            return null;
        }

        return User.state.personData.propertyId;
    }

    get getCorporationId(): number {
        if (!User.state.personData) {
            return null;
        }

        return User.state.personData.corporationId;
    }

    get getAccessToken() {
        return this.authData.accessToken;
    }

    get getPinInput() {
        return this.pinInput;
    }

    get getStaffNotifications() {
        return this.staffNotifications;
    }

    get getDashboardItemsPerPageCount() {
        return this.dashboardItemsPerPageCount;
    }

    get getWrongPinCounter() {
        return this.wrongPinCounter;
    }

    @Mutation
    public UPDATE_CACHED_PERSON_DATA(userData: any) {
        const arrayIndex = this.userCacheMap[userData.id];
        this.userCache[arrayIndex].setPersonalData(userData);
    }

    @Mutation
    public ADD_USER_TO_CACHE(userData: UserResponseData) {
        let arrayIndex = null;

        if (!this.userCacheMap.hasOwnProperty(userData.userId)) {
            arrayIndex = this.userCache.push(new PersonData(
                userData.userId,
                userData.firstName,
                userData.familyName,
                userData.phoneNumber,
            )) - 1;

            this.userCacheMap[userData.userId] = arrayIndex;
        } else {
            arrayIndex = this.userCacheMap[userData.userId];
        }

        if (userData.roleId) {
            this.userCache[arrayIndex].roleId = userData.roleId;
        }

        if (typeof userData.isTenantContactableByMessage !== 'undefined' && typeof userData.isTenantContactableByPhone !== 'undefined' && typeof userData.isTenantContactableOnFacilityBooking !== 'undefined') {
            this.userCache[arrayIndex].isTenantContactableByMessage             = userData.isTenantContactableByMessage;
            this.userCache[arrayIndex].isTenantContactableByPhone               = userData.isTenantContactableByPhone;
            this.userCache[arrayIndex].isTenantContactableOnFacilityBooking     = userData.isTenantContactableOnFacilityBooking;
        }

        if (typeof userData.translations !== 'undefined') {
            this.userCache[arrayIndex].translations = userData.translations;
        }

        if (userData.functionDescription) {
            this.userCache[arrayIndex].functionDescription = userData.functionDescription;
        }

        if (userData.phoneNumber) {
            this.userCache[arrayIndex].phoneNumber = userData.phoneNumber;
        }

        if (
            !this.userCache[arrayIndex].hasProfileImageData
            && userData.profileImage
            && userData.profileImageThumbnail
        ) {
            this.userCache[arrayIndex].setProfileImage(userData.profileImage);
            this.userCache[arrayIndex].setProfileImageThumbnail(userData.profileImageThumbnail);
        }

        if (userData.hasOwnProperty('roleId')) {
            this.userCache[arrayIndex].role = this.userRoles[this.userRolesMap[userData.roleId]];
        }

        return this.userCacheMap[arrayIndex];
    }

    @Mutation
    public ADD_USERS_TO_CACHE(userData: UserResponseData[]) {
        let arrayIndex = null;

        for (const user of userData) {
            if (!this.userCacheMap.hasOwnProperty(user.userId)) {
                arrayIndex = this.userCache.push(new PersonData(
                    user.userId,
                    user.firstName,
                    user.familyName,
                    user.phoneNumber,
                )) - 1;

                this.userCacheMap[user.userId] = arrayIndex;
            } else {
                arrayIndex = this.userCacheMap[user.userId];
            }

            if (user.title) {
                this.userCache[arrayIndex].staffTitle = user.title;
            }

            if (typeof user.isTenantContactableByMessage !== 'undefined' && typeof user.isTenantContactableByPhone !== 'undefined' && typeof user.isTenantContactableOnFacilityBooking !== 'undefined') {
                this.userCache[arrayIndex].isTenantContactableByMessage         = user.isTenantContactableByMessage;
                this.userCache[arrayIndex].isTenantContactableByPhone           = user.isTenantContactableByPhone;
                this.userCache[arrayIndex].isTenantContactableOnFacilityBooking = user.isTenantContactableOnFacilityBooking;
            }

            if (typeof user.translations !== 'undefined') {
                this.userCache[arrayIndex].translations = user.translations;
            }

            if (user.roleId) {
                this.userCache[arrayIndex].roleId = user.roleId;
            }

            if (user.functionDescription) {
                this.userCache[arrayIndex].functionDescription = user.functionDescription;
            }

            if (user.phoneNumber) {
                this.userCache[arrayIndex].phoneNumber = user.phoneNumber;
            }

            if (
                !this.userCache[arrayIndex].hasProfileImageData
                && user.profileImage !== null
                && user.profileImageThumbnail !== null
            ) {
                this.userCache[arrayIndex].setProfileImage(user.profileImage);
                this.userCache[arrayIndex].setProfileImageThumbnail(user.profileImageThumbnail);
            }

            if (user.hasOwnProperty('roleId')) {
                this.userCache[arrayIndex].role = this.userRoles[this.userRolesMap[user.roleId]];
            }
        }
    }

    @Mutation
    public UPDATE_SELECTED_LANGUAGE_CODE(languageCode: string): void {
        this.selectedLanguage = languageCode;
    }

    @Mutation
    public UPDATE_CORPORATION_DEFAULT_LANGUAGE(langaugeCode: string): void {
        this.corporationDefaultLanguage = langaugeCode;
    }

    @Mutation
    public UPDATE_RESPONSE_CODE(value: number | null) {
        this.responseCode = value;
    }

    @Mutation
    public LOGGED_IN(value: boolean): void {
        this.loggedIn = true;
    }

    @Mutation
    public LOGGED_OUT(): void {
        deleteState();

        store.commit(Mutations.RESET_MESSAGES_STORE_STATE);                 // Messages,
        store.commit(Mutations.RESET_CAMERA_STORE_STATE);                   // Camera,
        store.commit(Mutations.RESET_IMAGES_STORE_STATE);                   // Images,
        store.commit(Mutations.RESET_COMMENTS_STORE_STATE);                 // Comments,
        store.commit(Mutations.RESET_NOTIFICATIONS_STORE_STATE);            // Notifications,
        store.commit(Mutations.RESET_EVENTS_STORE_STATE);                   // Events,
        store.commit(Mutations.RESET_DOCUMENTS_STORE_STATE);                // Documents,
        store.commit(Mutations.RESET_OPTIONS_STORE_STATE);                  // Options,
        store.commit(Mutations.RESET_FEEDBACK_STORE_STATE);                 // Feedback,
        store.commit(Mutations.RESET_BUILDINGS_STORE_STATE);                // Buildings,
        store.commit(Mutations.RESET_CORPORATIONS_STORE_STATE);             // Corporations,
        store.commit(Mutations.RESET_EDITOR_STORE_STATE);                   // Editor,
        store.commit(Mutations.RESET_PROJECTS_STORE_STATE);                 // Projects,
        store.commit(Mutations.RESET_PROPERTIES_STORE_STATE);               // Properties,
        store.commit(Mutations.RESET_VARIANTS_STORE_STATE);                 // Variants,
        store.commit(Mutations.RESET_LIKES_STORE_STATE);                    // Likes,
        store.commit(Mutations.RESET_HOME_ASSISTANT_STORE_STATE);           // HomeAssistant,
        store.commit(Mutations.RESET_ISSUE_REPORT_STORE_STATE);             // IssueReport,
        store.commit(Mutations.RESET_USER_TICKETS_ATTRIBUTES_STORE_STATE);  // TicketAttributes,
        store.commit(Mutations.RESET_LOADING_STORE_STATE);                  // Loading,
        store.commit(Mutations.RESET_ATTACHMENTS_STORE_STATE);              // Attachments,
        store.commit(Mutations.RESET_TAGS_STORE_STATE);                     // Tags,
        store.commit(Mutations.RESET_CATEGORIES_STORE_STATE);               // Categories,
        store.commit(Mutations.RESET_LOADING_STORE_STATE);                  // Loading,
        store.commit(Mutations.RESET_SOCKET_STORE_STATE);                   // Socket,
        store.commit(Mutations.RESET_USER_MANAGEMENT_STORE_STATE);          // UserManagement,
        store.commit(Mutations.RESET_TICKETS_STORE_STATE);                  // Tickets,
        store.commit(Mutations.RESET_WAITING_LIST_STORE_STATE);             // WaitingLists,
        store.commit(Mutations.RESET_EVENT_LOGS_STORE_STATE);               // EventLogs,
        store.commit(Mutations.RESET_USER_TICKETS_STORE_STATE);             // UserTickets,
        store.commit(Mutations.RESET_PRESENTATION_FLOWS_STORE_STATE);       // PresentationFlows,
        store.commit(Mutations.RESET_INTEGRATIONS_STORE_STATE);             // Integrations,
        store.commit(Mutations.SET_USER_PROFILES_STORE_STATE);              // UserProfiles,
        store.commit(Mutations.RESET_CONFIRM_POPUP_STORE_STATE);            // ConfirmPopup,
        store.commit(Mutations.RESET_STAFF_PROPERTIES_STORE_STATE);         // StaffProperties,
        store.commit(Mutations.RESET_SHARED_FACILITIES_STORE_STATE);        // SharedFacilities,
        store.commit(Mutations.RESET_ROLES_AND_PERMISSIONS);                // RolesAndPermissions,
        store.commit(Mutations.RESET_RECIPIENTS);                           // Recipients,
        store.commit(Mutations.RESET_PROPERTY_NOTES_STATE);                 // PropertyNotes,
        store.commit(Mutations.RESET_GROUPINGS_STORE_STATE);                // Groupings,
        store.commit(Mutations.RESET_UNIT_GROUPING_CHARTS_STORE_STATE);     // UnitGroupingCharts,
        store.commit(Mutations.RESET_TEMPLATE_RENDERER_STORE_STATE);        // TemplateRenderer,
        store.commit(Mutations.RESET_FILE_UPLOAD_STORE_STATE);              // FileUpload,
        store.commit(Mutations.RESET_OVERLAYS_STORE_STATE);                 // Overlays,
        store.commit(Mutations.RESET_USER_STORE_STATE);
        store.commit(Mutations.RESET_GLOBAL_OVERLAY_STORE_STATE);
        store.commit(Mutations.RESET_SCROLL_STORE_STATE);
        store.commit(Mutations.RESET_DASHBOARD_STORE_STATE);

        router.push('/');
    }

    @Mutation
    public UPDATE_FIRST_NAME(value: string): void {
        this.personData.firstName = value;
    }

    @Mutation
    public UPDATE_FAMILY_NAME(value: string): void {
        this.personData.familyName = value;
    }

    @Mutation
    public UPDATE_EMAIL(value: string): void {
        this.personData.email = value;
    }

    @Mutation
    public UPDATE_PASSWORD_EMAIL(value: string): void {
        this.email = value;
    }

    @Mutation
    public UPDATE_PASSWORD_RESET_EMAIL(value: string): void {
        this.resetPasswordEmail = value;
    }

    @Mutation
    public UPDATE_FIRM(value: string): void {
        this.personData.firm = value;
    }

    @Mutation
    public UPDATE_ADDRESS(value: string): void {
        this.personData.address = value;
    }

    @Mutation
    public UPDATE_CITY(value: string): void {
        this.personData.city = value;
    }

    @Mutation
    public UPDATE_COUNTRY(value: string): void {
        this.personData.country = value;
    }

    @Mutation
    public UPDATE_ZIPCODE(value: string): void {
        this.personData.zipCode = value;
    }

    @Mutation
    public UPDATE_PHONENUMBER(value: number): void {
        this.personData.phoneNumber = value;
    }

    @Mutation
    public UPDATE_PASSWORD(value: string): void {
        this.password = value;
    }

    @Mutation
    public UPDATE_AUTH_DATA(authData: AuthData): void {
        this.authData = authData;
        axios.defaults.headers.common.Authorization = 'Bearer ' + this.authData.accessToken;
    }

    @Mutation
    public UPDATE_PERSON_DATA(personData: PersonData): void {
        this.personData = personData;
    }

    @Mutation
    public ADD_USER_ROLES(userRoleData: any[]): void {
        let arrayIndex = null;

        for (const userRole of userRoleData) {
            if (!this.userRolesMap.hasOwnProperty(userRole.id)) {
                arrayIndex = this.userRoles.push(new UserRoleData(
                    userRole.id,
                    userRole.key,
                    userRole.name,
                    userRole.description,
                    userRole.is_staff,
                )) - 1;

                this.userRolesMap[userRole.id] = arrayIndex;
                window.hoc.userRoleMap[userRole.id] = userRole.key;
                window.hoc.userRoleKeys[userRole.key] = userRole.id;
            } else {
                arrayIndex = this.userRolesMap[userRole.id];
            }
        }
    }

    @Mutation
    public ADD_ASSOCIATED_USERS(userData: UserResponseData[]) {
        this.associatedUsers = [];

        for (const user of userData) {
            if (this.associatedUsers.indexOf(user.userId) === -1) {
                this.associatedUsers.push(user.userId);
            }
        }
    }

    @Mutation
    public PASSWORD_RESET_SUCCESS(): void {
        this.resettingPassword = false;
        this.resetPasswordEmailSent = true;

        if (!store.getters['Viewport/desktopLayout']) {
            router.push('login');
        }
    }

    @Mutation
    public PASSWORD_RESET_FAILURE(): void {
        this.resetPasswordEmailSent = false;
    }

    @Mutation
    public RESET_RESET_PASSWORD(): void {
        this.resettingPassword = false;
        this.resetPasswordEmailSent = false;
        this.resetPasswordEmail = '';
    }

    @Mutation
    public SET_CURRENT_PROFILE_IMAGE(imageData: string, thumbnailData: string = null): void {
        this.personData.profileImage = imageData;
        this.personData.profileImageThumbnail = (thumbnailData) ? thumbnailData : imageData;
    }

    @Mutation
    public SET_FIREBASE_TOKEN(token: Token): void {
        this.firebaseToken = token;
        this.firebaseConnected = true;
    }

    @Action
    public INITIALIZE_FIREBASE() {      
        App.getInfo().then((appInfo: AppInfo) => {
            // Register with Apple / Google to receive push via APNS/FCM
            PushNotifications.register().then(() => { FCM.subscribeTo({ topic: appInfo.id }); });

            // On succcess, we should be able to receive notifications
            PushNotifications.addListener('registration', (token: Token) => {
                if (store.state.Viewport.deviceInfo.platform === 'ios') {
                    FCM.getToken().then((result: any) => {
                        store.dispatch(Actions.UPDATE_FIREBASE_TOKEN, {
                            value: result.token,
                        });
                    });
                } else {
                    store.dispatch(Actions.UPDATE_FIREBASE_TOKEN, token);
                }
            });

            // Some issue with our setup and push will not work
            PushNotifications.addListener('registrationError', (error: any) => {
            });
    
            // Show us the notification payload if the app is open on our device
            PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
                if (typeof notification.data.type === 'string') {
                    switch (notification.data.type) {
                        case 'document':
                            store.dispatch(Actions.FETCH_USER_DOCUMENTS);
                            break;
                        case 'event':
                            break;
                        case 'message':
                            break;
                        case 'notification':
                            break;
                        default:
                            break;
                    }
                }
            });
    
            // Method called when tapping on a notification
            PushNotifications.addListener('pushNotificationActionPerformed', (notificationAction: ActionPerformed) => {
                if (typeof notificationAction.notification.data.type === 'string') {
                    switch (notificationAction.notification.data.type) {
                        case 'document':
                            store.dispatch(Actions.FETCH_USER_DOCUMENTS).then(() => {
                                router.push({
                                    name: 'document',
                                    params: {
                                        documentId: notificationAction.notification.data.id,
                                    },
                                });
                            });
                            break;
                        case 'event':
                            router.push({
                                name: 'event',
                                params: {
                                    eventId: notificationAction.notification.data.id,
                                },
                                query: {
                                    notification: notificationAction.notification.data.notificationId,
                                },
                            });
                            break;
                        case 'message':
                            store.dispatch(Actions.FETCH_CONVERSATIONS, {
                                forceUpdate: true,
                                vue: null,
                            }).then(() => {
                                store.commit(Mutations.UPDATE_ACTIVE_CONVERSATION, notificationAction.notification.data.conversation);
    
                                router.push({
                                    name: 'messages',
                                    params: {
                                        conversationId: notificationAction.notification.data.conversation,
                                        messageId: notificationAction.notification.data.id,
                                    },
                                });
                            });
                            break;
                        case 'notification':
                            router.push({
                                name: 'notification',
                                params: {
                                    notificationId: notificationAction.notification.data.id,
                                },
                            });
                            break;
                        case 'channel':
                            router.push({
                                name: 'user-channel', params: { channelId: notificationAction.notification.data.channel }
                            });
                            break;
                        case 'comment':
                            store.commit(Mutations.SET_COMMENTS_OVERLAY_VISIBILITY, true);
                            store.dispatch(Actions.FETCH_NOTIFICATION_COMMENTS, {vue: this, notificationId: notificationAction.notification.data.notificationId});
    
                            break;
    
                        default:
                            break;
                    }
                }
            });
        });




    }

    @Action
    public CHANGE_PASSWORD(data: ChangePasswordRequestData) {
        store.commit(Mutations.INITIALIZE_PASSWORD_CHANGE);

        axios.post(Endpoints.Users.CHANGE_PASSWORD, data)
            .then((response) => {
                store.commit(Mutations.SET_PASSWORD_CHANGE_RESPONSE, response.status);
                store.commit(Mutations.SHOW_CHANGE_PASSWORD, false);

                NotifyHelpers.notify('password_has_been_changed_successfully', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    switch (error.response.status) {
                        case 422:
                        case 400:
                            store.commit(Mutations.SET_PASSWORD_CHANGE_RESPONSE, error.response.status);
                            break;
                        default:
                            const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                            store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                            break;
                    }
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_USER_LANGUAGE(languageKey: string) {
        axios.get(Endpoints.Localization.LOCALIZATION_DATA(languageKey))
            .then((response) => {
                if (store.state.i18n.translations.hasOwnProperty(response.data.language.key)) {
                    store.dispatch('i18n/replaceLocale', {
                        locale: response.data.language.key,
                        translations: response.data.texts,
                    });
                } else {
                    store.dispatch('i18n/addLocale', {
                        locale: response.data.language.key,
                        translations: response.data.texts,
                    });
                }

                store.commit(Mutations.UPDATE_SELECTED_LANGUAGE_CODE, languageKey);
                store.commit(Mutations.SET_LOCALIZATION_KEY, response.data.language.key);
                store.dispatch('i18n/setLocale', {locale: languageKey});
                LocalizationHelpers.changePackagesLanguage(response.data.language.key);
            });
    }

    @Action
    public LOG_IN(vue: Vue) {
        store.commit(Mutations.SET_LOGGING_IN, true);

        const requestPayload = new FormData();

        requestPayload.append('grant_type', 'password');
        requestPayload.append('client_id', OAuth.CLIENT_ID);
        requestPayload.append('client_secret', OAuth.CLIENT_SECRET);
        requestPayload.append('username', User.state.email);
        requestPayload.append('password', User.state.password);
        requestPayload.append('scope', '');

        axios.post(Endpoints.Users.AUTH_ENDPOINT, requestPayload)
            .then((response) => {
                const authResponse = new AuthData(
                    response.data.access_token,
                    response.data.refresh_token,
                    response.data.token_type,
                    response.data.expires_in,
                );
                
                store.commit(Mutations.UPDATE_AUTH_DATA, authResponse);

                if (Connection.socketEnabled) {
                    vue.$echo.connector.options.auth.headers.Authorization = 'Bearer ' + response.data.access_token;
                }

                return axios.get(Endpoints.Users.USER_ENDPOINT);
            })
            .then(response => {
                const { selectedLanguage, corporationDefaultLanguage } = response.data;

                const language = selectedLanguage ? selectedLanguage : corporationDefaultLanguage;
                if (language) {
                    store.dispatch(Actions.SET_LANGUAGE, {language: language, vue: this});
                }
                
                if (selectedLanguage) {
                    store.commit(Mutations.UPDATE_SELECTED_LANGUAGE_CODE, selectedLanguage);
                }
                
                if (corporationDefaultLanguage) {
                    store.commit(Mutations.UPDATE_CORPORATION_DEFAULT_LANGUAGE, corporationDefaultLanguage);
                }

                return axios.get(Endpoints.Roles.USER_ROLES);
            })
            .then((response) => {
                store.commit(Mutations.ADD_USER_ROLES, response.data.data);

                store.dispatch(Actions.FETCH_IDENTITIES, {
                    fromLoginInterface: true,
                    redirectTo: 'identities-choose',
                    vue,
                });
            })
            .catch((error) => {
                if (error.response && error.response.status !== null) {
                    User.state.responseCode = error.response.status;
                    store.commit(Mutations.SET_VIEW, {
                        view: Views.LogIn,
                        viewMode: ViewModes.LogIn.ErrorView,
                    });
                } else {
                    User.state.responseCode = null;
                }

                store.commit(Mutations.SET_LOGGING_IN, false);
            });
    }

    @Action
    public LOG_IN_USING_SAVED_CREDENTIALS(vue: Vue) {
        store.commit(Mutations.SET_LOGGING_IN, true);

        axios.get(Endpoints.Roles.USER_ROLES)
            .then((response) => {
                store.commit(Mutations.ADD_USER_ROLES, response.data.data);
            })
            .then(() => {
                store.dispatch(Actions.FETCH_IDENTITIES, {
                    fromLoginInterface: true,
                    redirectTo: 'identities-choose',
                    vue,
                });
            })
            .catch((error) => {
                console.log(error);

                if (error.response && error.response.status !== null) {
                    User.state.responseCode = error.response.status;

                    store.commit(Mutations.SET_VIEW, {
                        view: Views.LogIn,
                        viewMode: ViewModes.LogIn.ErrorView,
                    });
                } else {
                    User.state.responseCode = null;
                }

                store.commit(Mutations.SET_LOGGING_IN, false);
            });
    }

    @Action
    public REFRESH_USER_DATA(vue: Vue) {
        axios.get(Endpoints.Roles.USER_ROLES)
            .then((response) => {
                store.commit(Mutations.ADD_USER_ROLES, response.data.data);
            }).then((response) => {
                store.dispatch(Actions.FETCH_IDENTITIES, {fromLoginInterface: false});

                return axios.get(Endpoints.Users.USER_ENDPOINT);
            })
            .then((response) => {

                if (response.data.selectedLanguage) {
                    store.commit(Mutations.UPDATE_SELECTED_LANGUAGE_CODE, response.data.selectedLanguage);
                }

                if (response.data.corporationDefaultLanguage) {
                    store.commit(Mutations.UPDATE_CORPORATION_DEFAULT_LANGUAGE, response.data.corporationDefaultLanguage);
                }

                User.state.responseCode = response.status;

                User.state.sandbox = response.data.sandbox;

                store.commit(Mutations.SET_EULA_ACCEPTED_AT, response.data.eula_accepted_at);

                const abuseActionDictionary = {
                    block: {
                        user: [],
                    },
                    hide: {
                        conversation: [],
                        message: [],
                    },
                    report: {
                        conversation: [],
                        message: [],
                    },
                };

                if (response.data.abuse_actions) {
                    for (const abuseAction of response.data.abuse_actions) {
                        if (!abuseActionDictionary[abuseAction.action_type][abuseAction.content_type].includes(abuseAction.content_id)) {
                            abuseActionDictionary[abuseAction.action_type][abuseAction.content_type].push(abuseAction.content_id);
                        }
                    }
                }

                store.commit(Mutations.SET_ABUSE_ACTIONS_DICTIONARY, abuseActionDictionary);

                const user = new PersonData(
                    response.data.id,
                    response.data.person.first_name,
                    response.data.person.family_name,
                    response.data.person.phone_number,
                    response.data.address ? response.data.address.street : '',
                    response.data.address ? response.data.address.zip : '',
                    response.data.address ? response.data.address.city : '',
                    response.data.email,
                    response.data.property_id,
                    response.data.corporation_id,
                    response.data.isTenant,
                    response.data.mainRole,
                    response.data.hococoStaff,
                );

                if (response.data.features.length > 0) {
                    user.setFeatures(response.data.features);
                }

                user.hasPin = response.data.hasPin;

                user.setProfileImage(response.data.profileImage);
                user.setProfileImageThumbnail(response.data.profileImageThumbnail);

                user.setResidentPropertyData(response.data.propertyData);

                User.state.propertyVariantId = response.data.variantId;

                if (response.data.propertyRoleId !== null) {
                    const roleId = response.data.propertyRoleId;
                    user.role = User.state.userRoles[User.state.userRolesMap[roleId]];
                }
                
                store.commit(Mutations.UPDATE_PERSON_DATA, user);
                store.commit(Mutations.SET_CORPORATION_SQUARE_LOGO_URL, response.data.corporationId.secondary_logo_cdn);
                store.commit(Mutations.LOGGED_IN, true);

                store.dispatch(Actions.FETCH_CORPORATIONS);
                store.dispatch(Actions.FETCH_ADMIN_CURRENT_CORPORATION);
                store.dispatch(Actions.FETCH_PROJECTS);
                store.dispatch(Actions.FETCH_BUILDINGS);
                store.dispatch(Actions.FETCH_VARIANTS);
                store.dispatch(Actions.FETCH_PROPERTIES);
                store.dispatch(Actions.FETCH_IDENTITIES_NOTIFICATIONS);

                if (response.data.isTenant) {
                    store.dispatch(Actions.FETCH_DASHBOARD_ITEMS_PER_PAGE_COUNT);
                    store.dispatch(Actions.FETCH_INQUIRY_TYPES);
                    store.dispatch(Actions.FETCH_INQUIRY_CATEGORIES);
                    store.dispatch(Actions.FETCH_INQUIRIES);
                } else {
                    store.dispatch(Actions.FETCH_EVENT_LOGS_UNSEEN_COUNT);
                }

                const language = User.state.selectedLanguage ? User.state.selectedLanguage : User.state.corporationDefaultLanguage;
                store.dispatch(Actions.SET_LANGUAGE, {language , vue: vue});
                return axios.get(Endpoints.Localization.LOCALIZATION_DATA(language));   

            })
            .then((response) => {
               
                if (store.state.Viewport.deviceInfo.platform !== 'web') {
                    store.dispatch(Actions.INITIALIZE_FIREBASE);
                }

                store.dispatch(Actions.FETCH_CONVERSATIONS, {vue, forceUpdate: true});
                store.dispatch(Actions.FETCH_FEEDBACK_EVENTS);
                store.commit(Mutations.SET_LOGGING_IN, false);

                if (User.state.personData.isTenant || store.getters['Viewport/mobileLayout'] === true) {
                    router.push('/user/dashboard');
                } else if (vue.$route.name === 'landing') {
                    router.push('/admin/projects');
                }

                store.dispatch(Actions.INIT_IDENTITIES_SOCKET, {userId: User.state.personData.id, vue});
            })
            .catch((error) => {
                console.log(error);

                if (error.response && error.response.status !== null) {
                    User.state.responseCode = error.response.status;

                    store.commit(Mutations.SET_VIEW, {
                        view: Views.LogIn,
                        viewMode: ViewModes.LogIn.ErrorView,
                    });
                } else {
                    User.state.responseCode = null;
                }

                store.commit(Mutations.SET_LOGGING_IN, false);
            });
    }

    @Action
    public SET_LANGUAGE(data: {language: string, vue: Vue}) {
        return axios.get(Endpoints.Localization.LOCALIZATION_DATA(data.language))
            .then((response) => {
                if(data.vue.$i18n) {
                    if (data.vue.$i18n.localeExists(response.data.language.key)) {
                        data.vue.$i18n.replace(response.data.language.key, response.data.texts);
                    } else {
                        data.vue.$i18n.add(response.data.language.key, response.data.texts);
                        data.vue.$i18n.set(response.data.language.key);
                    }
                }

                store.dispatch('i18n/setLocale', {locale: response.data.language.key});
                store.commit(Mutations.SET_LOCALIZATION_KEY, response.data.language.key);
                LocalizationHelpers.changePackagesLanguage(response.data.language.key);
        });
    }


    @Action
    public SET_PIN(pin: string) {
        axios.post(Endpoints.Users.SET_PIN, {pin})
            .then((response) => {
                console.log(response);
            })
            .catch((error) => {
                if (error.response && error.response.status !== null) {
                    User.state.responseCode = error.response.status;

                    store.commit(Mutations.SET_VIEW, {
                        view: Views.LogIn,
                        viewMode: ViewModes.LogIn.ErrorView,
                    });
                } else {
                    User.state.responseCode = null;
                }
            });
    }

    @Action
    public RESET_PIN() {
        axios.post(Endpoints.Users.RESET_PIN, {}, {
            headers: {
                Authorization: 'Bearer ' + store.state.User.authData.accessToken,
            },
        }).then((response) => {
                store.dispatch(Actions.LOG_OUT);
            })
            .catch((error) => {
                if (error.response && error.response.status !== null) {
                    User.state.responseCode = error.response.status;

                    store.commit(Mutations.SET_VIEW, {
                        view: Views.LogIn,
                        viewMode: ViewModes.LogIn.ErrorView,
                    });
                } else {
                    User.state.responseCode = null;
                }
            });
    }

    @Action
    public VALIDATE_PIN(vue: Vue) {
        // vue.$echo.connector.options.auth.headers.Authorization = 'Bearer ' + User.state.authData.accessToken;
        axios.defaults.headers.common.Authorization = 'Bearer ' + User.state.authData.accessToken;

        axios.post(Endpoints.Users.VALIDATE_PIN, { pin: User.state.pinInput.join('') })
            .then((response) => {
                store.dispatch(Actions.LOG_IN_USING_SAVED_CREDENTIALS, vue);
            })
            .catch((error) => {
                store.commit(Mutations.SET_WRONG_PIN_COUNTER, 'increase');

                if (error.response && error.response.status !== null) {
                    User.state.responseCode = error.response.status;

                    store.commit(Mutations.SET_VIEW, {
                        view: Views.LogIn,
                        viewMode: ViewModes.LogIn.ErrorView,
                    });
                } else {
                    User.state.responseCode = null;
                }
            });
    }

    @Action
    public SET_AXIOS_AUTH_HEADERS(accessToken: string | null) {
        if (accessToken !== null) {
            axios.defaults.headers.common.Authorization = 'Bearer ' + accessToken;
        } else {
            axios.defaults.headers.common.Authorization =
                'Bearer ' + User.state.authData.accessToken;
        }
    }

    @Action
    public LOG_OUT() {
        store.commit(Mutations.LOGGED_OUT);
    }

    @Action
    public HANDLE_401() {
        store.commit(Mutations.LOGGED_OUT);
    }

    @Action
    public QUERY_PERSON_SUGGESTIONS(searchQuery: string) {
        const requestData = {
            searchQuery,
        };

        User.state.personSuggestions = [];

        axios.post(Endpoints.Suggestions.USER_SUGGESTIONS, requestData)
            .then((response) => {
                for (const user of response.data.suggestions) {
                        if (user.id !== store.getters['User/getUserId']) {
                        store.commit(Mutations.ADD_USER_TO_CACHE, {
                            userId: user.id,
                            firstName: user.firstName,
                            familyName: user.familyName,
                            profileImageThumbnail: user.profileImageThumbnail,
                            roleId: user.roleId,
                        });

                        User.state.personSuggestions.push(user.id);
                    }

                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_ASSOCIATED_USERS() {
        axios.get(Endpoints.Suggestions.ASSOCIATED_USERS_LIST)
            .then((response) => {
                if (response.data !== null)  {
                    store.commit(Mutations.ADD_USERS_TO_CACHE, response.data.data);
                    store.commit(Mutations.ADD_ASSOCIATED_USERS, response.data.data);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_DASHBOARD_ITEMS_PER_PAGE_COUNT() {
        axios.get(Endpoints.Users.DASHBOARD_ITEMS_PER_PAGE_COUNT)
            .then((response) => {
                store.commit(Mutations.SET_DASHBOARD_ITEMS_PER_PAGE_COUNT, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_DASHBOARD_ITEMS_PER_PAGE_COUNT(count: any) {
        const requestData = {
            count,
        };
        axios.post(Endpoints.Users.DASHBOARD_ITEMS_PER_PAGE_COUNT, requestData)
            .then(() => {})
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPDATE_USER() {
        axios.patch(Endpoints.Users.USER_ENDPOINT, {
            user: {
                firstName: User.state.personData.firstName,
                familyName: User.state.personData.familyName,
                zip: User.state.personData.zipCode,
                city: User.state.personData.city,
                street: User.state.personData.address,
                email: User.state.personData.email,
                phoneNumber: User.state.personData.phoneNumber,
            },
        }).catch((error) => {
            if (error.response) {
                const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
            } else {
                console.log(error);
            }
        });
    }

    @Action
    public UPDATE_ADMIN() {
        axios.patch(Endpoints.Admin.Profile.ADMIN_ENDPOINT, {
            firstName: User.state.personData.firstName,
            familyName: User.state.personData.familyName,
            email: User.state.personData.email,
            phoneNumber: User.state.personData.phoneNumber,
        })
            .then((response) => {
                NotifyHelpers.notify('profile_date_has_been_updated_successfully', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    console.log( error.response.data);

                    switch (error.response.status) {
                        case 422:
                            store.commit(Mutations.SET_ADMIN_PROFILE_UPDATE_RESPONSE, error.response.data);
                            break;
                        default:
                            const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                            store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                            break;
                    }
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CLONE_CURRENT_USER() {
        User.state.userClone = JSON.parse(JSON.stringify(User.state.personData));
    }

    @Action
    public RESET_PASSWORD(data: any) {
        const requestData = typeof data === 'undefined' ? { email: User.state.resetPasswordEmail } : data;

        axios.post(Endpoints.Users.RESET_PASSWORD, requestData)
            .then((response) => {
                if (typeof data === 'undefined') {
                    if (response.data && response.data.status) {
                        if (response.data.status === true) {
                            store.commit(Mutations.PASSWORD_RESET_SUCCESS);
                        } else {
                            store.commit(Mutations.PASSWORD_RESET_FAILURE);
                        }

                        return;
                    }

                    store.commit(Mutations.PASSWORD_RESET_FAILURE);
                } else {
                    NotifyHelpers.notify('reset_password_has_been_sent', 'positive');
                }
            })
            .catch((error) => {
                store.commit(Mutations.PASSWORD_RESET_FAILURE);

                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SEND_USER_INVITATION(payload: {userId: number, projectId: number}) {
        axios.post(Endpoints.Admin.Tenants.APP_INVITATION(payload.projectId, payload.userId))
            .then((response) => {
                NotifyHelpers.notify('server_response', 'positive', response.data.message);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SAVE_PROFILE_IMAGE(imageData: string) {
        const requestData = {
            imageData,
        };

        axios.post(Endpoints.Users.PROFILE_IMAGE, requestData)
            .then((response) => {
                store.commit(Mutations.SET_CURRENT_PROFILE_IMAGE, imageData);

                NotifyHelpers.notify('profile_image_has_been_updated_successfully', 'positive');
                store.commit(Mutations.SET_NEW_PROFILE_PICTURE, null);

                return Promise.resolve();
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }

                return Promise.reject();
            });
    }

    @Action
    public FETCH_MINIATURE(userId: number) {
        const user = User.state.userCache[User.state.userCacheMap[userId]];

        if (User.state.miniaturesFetched !== true) {
            User.state.miniaturesFetched[userId] = true;

            axios.get(Endpoints.Users.MINIATURE(userId))
                .then((response) => {
                    if (!response.data.hasProfileImage) {
                        user.hasProfileImage = false;
                    } else {

                        if (user.miniatureImage === '' && response.data.miniature !== null) {
                            store.commit(Mutations.SET_MINIATURE_IMAGE, {
                                data: response.data.miniature,
                                userId,
                            });
                        }
                    }

                    user.fetchedMiniature = true;

                    return Promise.resolve();
                })
                .catch((error) => {
                    if (error.response) {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    } else {
                        console.log(error);
                    }

                    return Promise.reject();
                });
        }

    }

    @Action
    public UPDATE_FIREBASE_TOKEN(token: Token) {
        const requestData = {
            registrationToken: token.value,
        };

        axios.put(Endpoints.Users.FIREBASE_TOKEN, requestData)
            .then((response) => {
                store.commit(Mutations.SET_FIREBASE_TOKEN, token);
                return Promise.resolve();
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }

                return Promise.reject();
            });
    }

    @Action
    public GET_FLOOR_PLAN(variantId: number) {
        if (variantId < 1 || variantId === null) {
            return;
        }

        axios.get(Endpoints.Admin.Variants.FLOOR_PLAN(variantId))
            .then((response) => {
                User.state.floorPlanBase64 = response.data.imageData;
                User.state.variantWithFloorPlan = response.data.variantId;
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public GET_ADMIN_FLOOR_PLAN(variantId: number) {
        if (variantId < 1 || variantId === null) {
            return;
        }

        axios.get(Endpoints.Admin.Variants.FLOOR_PLAN(variantId))
            .then((response) => {
                User.state.floorPlanBase64 = response.data.imageData;
                User.state.variantWithFloorPlan = response.data.variantId;
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public TRACK_ACTION(data: TrackingData) {
        const requestData = {
            action: data.action,
            data: data.data,
            activeRoute: router.currentRoute.fullPath,
        };

        axios.put(Endpoints.Users.TRACK_ACTION, requestData)
            .then((response) => {
                return Promise.resolve();
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }

                return Promise.reject();
            });
    }

    @Action
    public GET_LAST_VERSION(vue: any) {
        const language = User.state.selectedLanguage ? User.state.selectedLanguage : User.state.corporationDefaultLanguage;

        store.dispatch(Actions.SET_LANGUAGE, { language, vue });

        axios.get(Endpoints.Versions.LAST_VERSION)
            .then((res: any) => {
                store.commit(Mutations.SET_LAST_VERSION, res.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public GET_AVAILABLE_UPDATE_URL(bundleId: string) {
        axios.post(Endpoints.Versions.AVAILABLE_UPDATE_URL(bundleId))
            .then((response: any) => {
                store.commit(Mutations.SET_AVAILABLE_UPDATE_URL, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public TOGGLE_STAFF_NOTIFICATIONS(data: any) {
        axios.post(Endpoints.Admin.Staff.STAFF_NOTIFICATIONS, data)
            .then(() => {})
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_STAFF_NOTIFICATIONS() {
        axios.get(Endpoints.Admin.Staff.STAFF_NOTIFICATIONS)
            .then((response) => {
                store.commit(Mutations.SET_STAFF_NOTIFICATIONS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public async GET_USER_LANGUAGE_CODE() {
        if (!User.state.selectedLanguage) {
            try {
                const language = await Device.getLanguageCode();
                
                if (!language) {
                    return;
                }

                const languageCode = language.value.split('-')[0];
                store.dispatch(Actions.FETCH_ALL_LANGUAGES).finally(async () => {
                    const allLanguages = await store.getters['Localization/getAllLanguages'];
                    if (allLanguages.includes(languageCode)) {
                        store.commit(Mutations.UPDATE_SELECTED_LANGUAGE_CODE, languageCode);
                    }
                });
            } catch(error) {
                console.log(error);
                return; 
            }
        }
    }


    @Mutation
    public SET_STAFF_NOTIFICATIONS(data: any) {
        this.staffNotifications = data;
    }

    @Mutation
    public SET_LAST_VERSION(data: any) {
        if (data) {
            this.lastVersion = data;
        } else {
            this.lastVersion = 'missing';
            this.availableUpdateVisible = false;
        }
    }

    @Mutation
    public SET_AVAILABLE_UPDATE_URL(url: string) {
        this.availableUpdateUrl = url;
    }

    @Mutation
    public SET_CURRENT_VERSION(version: string) {
        this.currentVersion = version;
    }

    @Mutation
    public SET_AVAILABLE_UPDATE_VISIBILITY(visible: boolean) {
        this.availableUpdateVisible = visible;
    }

    @Mutation
    public SET_MINIATURE_IMAGE(data: MiniatureImageOptions) {
        this.userCache[User.state.userCacheMap[data.userId]].miniatureImage = data.data;
        this.userCache[User.state.userCacheMap[data.userId]].hasMiniature = true;
    }

    @Mutation
    public INITIALIZE_PASSWORD_CHANGE() {
        this.changingPassword = true;
        this.passwordChangeResponseCode = null;
    }

    @Mutation
    public SET_PASSWORD_CHANGE_RESPONSE(responseCode: number) {
        this.passwordChangeResponseCode = responseCode;
    }

    @Mutation
    public SET_ADMIN_PROFILE_UPDATE_RESPONSE(response: any) {
        this.profileUpdateResponse = response;
    }

    @Mutation
    public SET_NEW_PROFILE_PICTURE(image: any) {
        this.newProfileImage = image;
    }

    @Mutation
    public COMPLETE_PASSWORD_CHANGE() {
        this.changingPassword = false;
        this.passwordChangeResponseCode = null;
    }

    @Mutation
    public SET_LOGGING_IN(value: boolean) {
        this.loggingIn = value;
    }

    @Mutation
    public SET_PIN_INPUT(value: string[]) {
        this.pinInput = value;
    }

    @Mutation
    public ADD_PIN_DIGIT(value: string) {
        this.pinInput.push(value);
    }

    @Mutation
    public SET_DASHBOARD_ITEMS_PER_PAGE_COUNT(count: any) {
        this.dashboardItemsPerPageCount = count;
    }

    @Mutation
    public SET_WRONG_PIN_COUNTER(type: string) {
        switch (type) {
            case 'increase':
                this.wrongPinCounter = this.wrongPinCounter + 1;
                break;
            case 'decrease':
                this.wrongPinCounter = this.wrongPinCounter - 1;
                break;
            case 'reset':
                this.wrongPinCounter = 0;
                break;
        }
    }
}

